import { placeOrder } from "common/features/cart/cartActions";
import { Cart, CartItem, CartTotal } from "common/features/cart/cartModels";
import { EmptyLocationDetails } from "common/features/cart/cartReducer";
import { DeliveryProviders } from "common/features/delivery/DeliveryModels";
import {
  deliveryDiscountSettings,
  Tax,
} from "common/features/restaurant/restaurantModels";
import { RootState } from "common/rootReducer";
import {
  getEditCartItemFromCartItem,
  getItemTax,
  getTotalByCode,
  mergeTotals,
} from "common/utils/cart-utils";
import {
  getDateString,
  getTimeString,
} from "common/utils/date-time-helper-utils";
import { getDistanceFromLatLonInKm } from "common/utils/distance-lat-long";
import {
  getDecimalPlacedPrice,
  getDecimalPlacedPriceString,
  getTwoDecimalValue,
} from "common/utils/menu-helper-utils";
import { addMinutes } from "date-fns";
import { useCallback, useState } from "react";
import { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { CHECKIN_TABLE_ID } from "common/constants";
import { useHistory, useParams, useLocation } from "react-router";

import {
  EditOrderItem,
  EditOrderRequestCart,
} from "common/features/order/orderModels";
import { updateOrderDetail } from "common/features/order/orderActions";

import { OrderTotal } from "common/features/order/orderModels";

const UpdateOrderTypes = ["OrderUpdate", "TotalUpdate"] as const;

export type UpdateOrderType = (typeof UpdateOrderTypes)[number];

export const usePlaceOrder = (): [
  () => void,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  (type: UpdateOrderType, updatedTipTotals?: CartTotal) => void,
  number,
  number,
  number
] => {
  const dispatch = useDispatch();
  let paramsData = useParams<any>();
  const restaurantId = useSelector(
    (state: RootState) => state.restaurant.currentRestaurantDetail.id
  );
  const restaurantOrderTypeId = useSelector(
    (state: RootState) => state.restaurant.restaurantOrderTypeId
  );

  const restaurantCountry = useSelector(
    (state: RootState) => state.restaurant.currentRestaurantDetail.country
  );
  const isASAP = useSelector((state: RootState) => state.cart.orderTimeASAP);
  const restaurantDefaultEta = useSelector(
    (state: RootState) => state.cart.cartRestaurantDetails?.defaultPickUpETA
  );

  const selectedOffer = useSelector(
    (state: RootState) => state.offer.selectedOffer
  );

  const cartRestaurantDetails = useSelector(
    (state: RootState) => state.restaurant.currentRestaurantDetail
  );

  let dineInOrderTypeDetails = cartRestaurantDetails.orderTypes?.filter(
    (e) => e.id === restaurantOrderTypeId
  )[0];
  const currentLocationDetails = useSelector((state: RootState) =>
    state.restaurant.currentRestaurantDetail.branch.filter((branch) => {
      return branch.id === cartRestaurantDetails.id;
    })
  );
  const orderDetail = useSelector(
    (state: RootState) => state.order.currentOrderDetail
  );

  const restaurantLocationDetails = useMemo(() => {
    return currentLocationDetails.length > 0 ? currentLocationDetails[0] : null;
  }, [currentLocationDetails]);

  const cart = useSelector((state: RootState) => state.cart.cart);

  const runningCartDetails = useSelector(
    (state: RootState) => state.cart.runningCart
  );

  const deliveryAddress = useSelector(
    (state: RootState) => state.cart.deliveryAddress
  );
  const UserAuthId = useSelector((state: RootState) => state.auth.user?.id);

  const guestUser = useSelector((state: RootState) => state.auth.guestUser);

  const tipValue = useSelector((state: RootState) => state.cart.tipValue);
  const tipType = useSelector((state: RootState) => state.cart.tipType);

  const orderNote = useSelector((state: RootState) => state.cart.cart.comment);
  const placeOrderLoading = useSelector(
    (state: RootState) => state.cart.placeOrderLoading
  );

  const restaurantDetails = useSelector(
    (state: RootState) => state.cart.cartRestaurantDetails
  );

  const platformConfiguration = useSelector(
    (state: RootState) =>
      state.cart.cartRestaurantDetails?.platformConfiguration
  );

  const orderApiTotals = useSelector(
    (state: RootState) => state.order.totals
  );

  const orderItems = useSelector(
    (state: RootState) => state.order.orderItems
  );

  const orderTotal = useSelector(
    (state: RootState) => state.order.orderTotal
  );

  const checkInTableId = localStorage.getItem(CHECKIN_TABLE_ID);

  const tableId = paramsData.tableId
    ? paramsData.tableId
    : checkInTableId
    ? checkInTableId
    : null;
  const restaurantOrderTax = useSelector(
    (state: RootState) => state.cart.cartRestaurantOrderTax
  );

  const convCharge = useSelector(
    (state: RootState) => state.restaurant.restaurantOrderTax
  );

  const deliveryDistance = useSelector(
    (state: RootState) => state.cart.cart.deliveryDistance
  );

  const totalItemsubTotal = useSelector((state: RootState) =>
    state.cart.cart.items.reduce(
      (accumulator: number, currentValue: CartItem) => {
        return accumulator + parseFloat(currentValue.subTotal);
      },
      0
    )
  );

  const deliveryQuotationDetails = useSelector(
    (state: RootState) => state.delivery.deliveryQuotationDetails
  );

  const initialDeliveryKm = useSelector(
    (state: RootState) =>
      state.restaurant.currentRestaurantDetail.deliverySettings?.initialKm
  );

  const initialDeliveryCharge = useSelector(
    (state: RootState) =>
      state.restaurant.currentRestaurantDetail.deliverySettings?.initialCost
  );

  const maximumDeliveryFee = useSelector(
    (state: RootState) =>
      state.restaurant.currentRestaurantDetail.deliverySettings?.maxDeliveryFee
  );

  const deliveryAdditionalCharge = useSelector(
    (state: RootState) =>
      state.restaurant.currentRestaurantDetail.deliverySettings?.additionalCost
  );
  const restaurantOrderTypes = useSelector(
    (state: RootState) => state.restaurant.currentRestaurantDetail.orderTypes
  );

  const getCalculatedValue = (amount: number, value: any) => {
    if (value.type == "FLATFEE") {
      return value.rate;
    } else if (value.type == "PERCENT") {
      return amount * (value.rate / 100);
    } else {
      return 0;
    }
  };

  const isContactlessDineInOrder = useMemo(() => {
    if (restaurantOrderTypes) {
      return (
        (restaurantDetails && restaurantDetails.dining == 2) ||
        (restaurantDetails && restaurantDetails.dining == 3)
      );
    }
    return false;
  }, [restaurantOrderTypeId, restaurantOrderTypes, restaurantDetails]);

  const serviceCharge: number = useMemo(() => {
    return 0;
  }, [totalItemsubTotal, restaurantOrderTax, isContactlessDineInOrder]);

  const serviceTaxData: any = [];

  const availWalletAmount = useSelector(
    (state: RootState) => state.offer.availWalletAmount
  );
  const getCountryBasedPrice = (price: number) => {
    return restaurantCountry == "US" ? price : Math.round(price);
  };

  // Since we are creating a new batch for contactless dinein Order , sending modified Items Ids  as empty array.
  const mergedItems = useMemo(() => {
    return [
      // ...getEditCartItemFromCartItem(runningCartDetails.items, []),
      ...getEditCartItemFromCartItem(cart.items, []),
    ] as EditOrderItem[];
  }, [cart, runningCartDetails, orderDetail]);

  const getAdditionalDeliveryCharge = () => {
    if (
      initialDeliveryCharge &&
      initialDeliveryKm &&
      deliveryAdditionalCharge
    ) {
      return (
        initialDeliveryCharge +
        Math.ceil(deliveryDistance - initialDeliveryKm) *
          deliveryAdditionalCharge
      );
    }
  };
  const isDineInOrder = useMemo(() => {
    return (
      dineInOrderTypeDetails &&
      dineInOrderTypeDetails.typeGroup === "D" &&
      tableId
    );
  }, [dineInOrderTypeDetails, tableId]);

  const isContactlessDinein = useMemo(() => {
    return restaurantDetails && restaurantDetails.dining == 2;
  }, [restaurantDetails]);

  const isContactlessDineinV2 = useMemo(() => {
    return restaurantDetails && restaurantDetails.dining == 3;
  }, [restaurantDetails]);

  const deliveryCharges = useMemo(() => {
    if (
      deliveryDistance &&
      restaurantDetails &&
      restaurantDetails.deliverySettings &&
      initialDeliveryKm &&
      initialDeliveryCharge &&
      maximumDeliveryFee
    ) {
      if (deliveryDistance < initialDeliveryKm) {
        return initialDeliveryCharge;
      } else if (deliveryDistance >= initialDeliveryKm) {
        let additionalDeliveryCharge = getAdditionalDeliveryCharge();
        if (
          additionalDeliveryCharge &&
          additionalDeliveryCharge > maximumDeliveryFee
        ) {
          return maximumDeliveryFee;
        } else {
          return additionalDeliveryCharge;
        }
      }
    } else {
      return 0;
    }
  }, [deliveryDistance, restaurantDetails]);

  const discountAmount = useMemo(() => {
    let amount = 0;
    if (
      selectedOffer &&
      totalItemsubTotal >= selectedOffer?.minOrderAmount &&
      selectedOffer.offerType === "PERCENT"
    ) {
      let discount = (selectedOffer.offerRate / 100) * totalItemsubTotal;
      amount =
        discount > selectedOffer.maxDiscount
          ? selectedOffer.maxDiscount
          : discount;
    } else if (
      selectedOffer &&
      totalItemsubTotal >= selectedOffer?.minOrderAmount
    ) {
      amount = selectedOffer?.maxDiscount;
    }
    return amount;
  }, [selectedOffer, totalItemsubTotal]);

  const cartItems: CartItem[] = cart.items.map((item) => {
    return {
      ...item,
      taxFees: getItemTax(item, serviceTaxData).toString(),
    };
  });
  const walletAmount = useSelector(
    (state: RootState) => state.user.currentUserDetail.walletAmount
  );

  const deliverySetting = useMemo(() => {
    return restaurantDetails?.deliverySettings;
  }, [restaurantDetails]);

  const deliveryDiscountSettings = useMemo(() => {
    return deliverySetting?.deliveryDiscountSettings;
  }, [deliverySetting]);

  // const totalRestaurantCharges =
  //   restaurantOrderTax && restaurantOrderTax.length > 0
  //     ? restaurantOrderTax.reduce((accumulator: number, currentCharge: Tax) => {
  //         if (currentCharge.type == "FLATFEE") {
  //           return accumulator + currentCharge.rate;
  //         } else if (currentCharge.type == "PERCENT") {
  //           return accumulator + totalItemsubTotal * (currentCharge.rate / 100);
  //         } else {
  //           return accumulator;
  //         }
  //       }, 0)
  //     : 0;

  const isDeliveryOrder = useMemo(() => {
    let filteredOrderTypes = restaurantDetails
      ? restaurantDetails.orderTypes?.filter(
          (orderType) => orderType.id == restaurantOrderTypeId
        )
      : [];
    return filteredOrderTypes && filteredOrderTypes.length > 0
      ? filteredOrderTypes[0].typeGroup == "S"
      : false;
  }, [restaurantDetails, restaurantOrderTypeId]);

  const isPickupOrder = restaurantDetails?.orderTypes?.filter(
    (e) => e.id === restaurantOrderTypeId
  )[0];

  const packagingCharges = useMemo(() => {
    const packagingCharge = restaurantOrderTax.filter(
      (u: Tax) => u.name === "PACKAGING_CHARGE"
    );
    if (packagingCharge.length > 0) {
      return getCalculatedValue(totalItemsubTotal, packagingCharge[0]);
    } else return 0;
  }, [restaurantOrderTax, totalItemsubTotal]);

  const packagingCharge = useMemo(() => {
    const restaurantOrderTaxEnabled = restaurantOrderTax.length > 0;
    if (isDeliveryOrder && platformConfiguration && restaurantDetails) {
      // Delivery Orders
      const thirdPartyDeliveryPartners = [
        DeliveryProviders.DUNZO,
        DeliveryProviders.SHADOWFAX,
        DeliveryProviders.SIMPERY,
        DeliveryProviders.DOORDASH,
      ];
      const magilFlatFeeForDelivery =
        platformConfiguration &&
        platformConfiguration.magilDeliveryFee &&
        restaurantDetails.country == "US"
          ? platformConfiguration.magilDeliveryFee &&
            platformConfiguration.magilDeliveryFee.us
          : platformConfiguration.magilDeliveryFee &&
            platformConfiguration.magilDeliveryFee.ind;
      if (
        restaurantDetails &&
        restaurantDetails.locationDeliveryProviders &&
        restaurantDetails.locationDeliveryProviders?.deliveryProviderId &&
        thirdPartyDeliveryPartners.includes(
          restaurantDetails.locationDeliveryProviders.deliveryProviderId
        ) &&
        deliveryQuotationDetails
      ) {
        // Thirdparty delivery
        return deliveryQuotationDetails.estimatedPrice
          ? parseFloat(deliveryQuotationDetails.estimatedPrice) +
              magilFlatFeeForDelivery.thirdPartyDelivery
          : magilFlatFeeForDelivery.thirdPartyDelivery;
      } else if (deliverySetting?.isInHouseDelivery) {
        // Inhouse delivery
        if (deliverySetting.chargePerKm > 0) {
          // computing delivery fee without Magil's flatfee
          const computedChargePerKmFee = restaurantOrderTaxEnabled
            ? deliveryDistance *
              (getCalculatedValue(totalItemsubTotal, restaurantOrderTax[0]) /
                deliverySetting.chargePerKm)
            : 0;
          // Amount per distance fee ,  Inhouse delivery
          if (deliverySetting.maxDeliveryFee > 0) {
            return deliverySetting.maxDeliveryFee > computedChargePerKmFee
              ? computedChargePerKmFee + magilFlatFeeForDelivery.inhouseDelivery
              : deliverySetting.maxDeliveryFee;
          } else {
            // max Delivery fee is not configured
            return (
              computedChargePerKmFee + magilFlatFeeForDelivery.inhouseDelivery
            );
          }
        } else {
          // Flatfee ,  Inhouse delivery
          return deliveryCharges
            ? deliveryCharges + magilFlatFeeForDelivery.inhouseDelivery
            : magilFlatFeeForDelivery.inhouseDelivery;
        }
      } else {
        // if not third party and inhousedelivery
        // return restaurantOrderTaxEnabled
        //   ? getCalculatedValue(totalItemsubTotal, restaurantOrderTax[0])
        //   : 0;
        return deliveryCharges
          ? deliveryCharges + magilFlatFeeForDelivery.inhouseDelivery
          : magilFlatFeeForDelivery.inhouseDelivery;
      }
    }
    //  else if (isPickupOrder && isPickupOrder?.typeGroup === "P") {
    //   return serviceCharge;
    // }
    else {
      return deliveryCharges ? deliveryCharges : packagingCharges;
    }
  }, [
    restaurantOrderTax,
    totalItemsubTotal,
    deliveryQuotationDetails,
    restaurantDetails,
    deliveryDistance,
    isDeliveryOrder,
  ]);

  const packagingChargeTax = useMemo(() => {
    if (restaurantOrderTax.length > 0) {
      return packagingCharge;
    } else {
      return 0;
    }
  }, [packagingCharge, restaurantOrderTax]);

  const totalItemTaxAmount = useSelector((state: RootState) =>
    state.cart.cart.items.reduce(
      (accumulator: number, currentItem: CartItem) => {
        return accumulator + getItemTax(currentItem, serviceTaxData);
      },
      0
    )
  );
  const getAmountInDecimal = (amount: Number) => {
    const fullValue = amount* 100;  
    return parseFloat(Math.trunc(fullValue)/100).toFixed(2);
  };

  let totalTaxAmount: number = getDecimalPlacedPrice(
    Number(getAmountInDecimal(totalItemTaxAmount))
  );

  let totalTaxAndCharges: number = getDecimalPlacedPrice(
    getAmountInDecimal(totalTaxAmount + packagingCharge)
  );
  let tipAmount =
    tipType == "FLATFEE"
      ? tipValue
      : tipValue > 0
      ? getTwoDecimalValue(
        ((getTwoDecimalValue(tipValue) / 100) * getTwoDecimalValue(totalItemsubTotal)))
      : 0;
  // getDecimalPlacedPrice((totalItemsubTotal *  tipValue) / 100)

  const selectedOfferType = selectedOffer?.offerType;

  const getDeliverFeeReductionAmount = (
    totalAmount: number,
    deliveryCharge: number
  ) => {
    if (
      isDeliveryOrder &&
      deliveryDiscountSettings &&
      deliverySetting?.isDeliveryDiscountAvailable
    ) {
      const appliedFeeSetting = deliveryDiscountSettings.find(
        (setting: deliveryDiscountSettings) => {
          return (
            totalAmount > setting.minAmount &&
            (setting.maxAmount != 0 ? totalAmount <= setting.maxAmount : true)
          );
        }
      );
      return appliedFeeSetting
        ? getDecimalPlacedPrice(
            deliveryCharge * (appliedFeeSetting.percent / 100)
          )
        : 0;
    }
    return 0;
  };

  const convenienceFee = useMemo(() => {
    const convenienceCharge = restaurantOrderTax.filter(
      (u: Tax) => u.name === "CONVENIENCE_FEE"
    );
    if (convenienceCharge.length > 0) {
      return getAmountInDecimal(
        getCalculatedValue(totalItemsubTotal, convenienceCharge[0])
      );
    } else return 0;
  }, [restaurantOrderTax, totalItemsubTotal, totalTaxAndCharges]);
  // Offers provided by merchant on delivery orders
  const deliveryFeeSettingAmount = useMemo(() => {
    if (isDeliveryOrder) {
      return getDeliverFeeReductionAmount(
        getDecimalPlacedPrice(
          totalItemsubTotal + totalTaxAndCharges + tipAmount
        ),
        getDecimalPlacedPrice(packagingCharge)
      );
    } else {
      return 0;
    }
  }, [
    packagingCharge,
    isDeliveryOrder,
    totalItemsubTotal,
    totalTaxAndCharges,
    tipAmount,
  ]);

  const getWalletValue = (walletAmount: number, orderAmount: number) => {
    if (walletAmount >= orderAmount) {
      return (
        orderAmount -
        (walletAmount -
          (walletAmount -
            getCountryBasedPrice(
              getDecimalPlacedPrice(
                totalItemsubTotal +
                  totalTaxAndCharges +
                  tipAmount -
                  deliveryFeeSettingAmount
              )
            )))
      );
    } else {
      return orderAmount - walletAmount;
    }
  };

  let totalOrderPrice =
    selectedOffer && availWalletAmount
      ? getWalletValue(
          walletAmount,
          getCountryBasedPrice(
            getDecimalPlacedPrice(
              totalItemsubTotal +
                totalTaxAndCharges +
                convenienceFee +
                tipAmount -
                discountAmount -
                deliveryFeeSettingAmount
            )
          )
        )
      : selectedOffer
      ? getCountryBasedPrice(
          getDecimalPlacedPrice(
            totalItemsubTotal +
              totalTaxAndCharges +
              convenienceFee +
              tipAmount -
              discountAmount -
              deliveryFeeSettingAmount
          )
        )
      : walletAmount && availWalletAmount
      ? getWalletValue(
          walletAmount,
          getCountryBasedPrice(
            getDecimalPlacedPrice(
              totalItemsubTotal +
                totalTaxAndCharges +
                convenienceFee +
                tipAmount -
                deliveryFeeSettingAmount
            )
          )
        )
      : getCountryBasedPrice(
          getAmountInDecimal(totalItemsubTotal) +
            getAmountInDecimal(totalTaxAndCharges) +
            getAmountInDecimal(convenienceFee) +
            getAmountInDecimal(tipAmount) -
            getAmountInDecimal(deliveryFeeSettingAmount)
        );
  const redeemedWalletAmt: number =
    walletAmount >= walletAmount + totalOrderPrice // 100 > 160
      ? walletAmount -
        (walletAmount -
          getCountryBasedPrice(
            getDecimalPlacedPrice(
              totalItemsubTotal +
                totalTaxAndCharges +
                convenienceFee +
                tipAmount -
                deliveryFeeSettingAmount
            )
          ))
      : walletAmount;

  const getDiscountValue = () => {
    if (selectedOffer && availWalletAmount)
      return (discountAmount + redeemedWalletAmt).toString();
    else if (availWalletAmount)
      return (redeemedWalletAmt + discountAmount).toString();
    else if (selectedOffer) return discountAmount.toString();
    else if (discountAmount && discountAmount) return discountAmount.toString();
    else return "";
  };
  const discountTotal: CartTotal[] = [
    {
      code: "1",
      sortOrder: "1",
      title: "Item Total",
      value: getDecimalPlacedPriceString(totalItemsubTotal), // Without Tax
    },
    {
      code: "2",
      sortOrder: "2",
      title: "Tax",
      value: totalTaxAmount.toString(), // Item Tax + Restaurant Charge Tax + Service CHarge
    },
    {
      code: "3",
      sortOrder: "3",
      title: "Tip",
      value: tipAmount.toString(),
    },
    {
      code: "4",
      sortOrder: "4",
      title: "Additional Charges",
      value: getDecimalPlacedPriceString(packagingCharge),
    },
    {
      code: "5",
      sortOrder: "7",
      title: "Grand Total",
      value: availWalletAmount
        ? (totalOrderPrice + walletAmount).toString()
        : totalOrderPrice.toString(),
    },
    {
      code: "6",
      sortOrder: "5",
      title: "Discount",
      value: getDiscountValue() || "0",
    },
    {
      code: "7",
      sortOrder: "6",
      title: "Convenience Fee",
      value: getDecimalPlacedPriceString(convenienceFee),
    },
  ];

  const totals: CartTotal[] = [
    {
      code: "1",
      sortOrder: "1",
      title: "Item Total",
      value: getDecimalPlacedPriceString(totalItemsubTotal), // Without Tax
    },
    {
      code: "2",
      sortOrder: "2",
      title: "Tax",
      value: totalTaxAmount.toString(), // Item Tax + Restaurant Charge Tax + service Charge
    },
    {
      code: "3",
      sortOrder: "3",
      title: "Tip",
      value: tipAmount.toString(),
    },
    {
      code: "4",
      sortOrder: "4",
      title: "Additional Charges",
      value: getDecimalPlacedPriceString(packagingCharge),
    },

    {
      code: "5",
      sortOrder: "6",
      title: "Grand Total",
      // value: totalOrderPrice.toString(),
      value: availWalletAmount
        ? (totalOrderPrice + walletAmount).toString()
        : totalOrderPrice.toString(),
    },
    {
      code: "7",
      sortOrder: "5",
      title: "Convenience Fee",
      value: getDecimalPlacedPriceString(convenienceFee),
    },
  ];

  const getWindowedDateString = () => {
    return getDateString(
      addMinutes(new Date(), restaurantDefaultEta || 0).toISOString()
    );
  };

  const getWindowedTimeString = () => {
    return getTimeString(
      addMinutes(new Date(), restaurantDefaultEta || 0).toISOString()
    );
  };

  const pickUpDate =
    !cart.pickUpDate || cart.pickUpDate.length == 0 || isASAP
      ? getWindowedDateString()
      : cart.pickUpDate;

  const deliveryDate =
    !cart.deliveryDate || cart.deliveryDate.length == 0 || isASAP
      ? getWindowedDateString()
      : cart.deliveryDate;

  const locationDetails = deliveryAddress
    ? deliveryAddress
    : EmptyLocationDetails;

  const mergedTotals = useMemo(() => {
    return [
      ...mergeTotals(
        selectedOffer ? discountTotal : totals,
        orderDetail?.totals || []
      ),
    ];
  }, [
    orderDetail,
    runningCartDetails,
    cart,
    selectedOffer,
    discountTotal,
    totals,
    tipAmount,
  ]);

  const makeOrder = () => {
    const orderDetails = {
      locationId: restaurantId,
      customerId: UserAuthId,
      deviceId: "",
      staffId: "",
      fullName: guestUser
        ? guestUser?.fullName
        : localStorage.getItem("USER_NAME")
        ? localStorage.getItem("USER_NAME")
        : localStorage.getItem("GUEST_USER_NAME") || "",
      email: guestUser
        ? guestUser?.email
        : localStorage.getItem("GUEST_EMAIL") || "",
      phone: guestUser
        ? guestUser?.mobile
        : localStorage.getItem("MOBILE_NUMBER")
        ? localStorage.getItem("MOBILE_NUMBER")
        : localStorage.getItem("GUEST_MOBILE_NUMBER") || "",
      totalItems: cart.items.length.toString(),
      comment: orderNote,
      orderTypeId: restaurantOrderTypeId, // ID for PickUp / Delivery / PreOrder
      orderTotal: orderTotal,
      //   orderTotal: totalOrderPrice.toString(), //number
      items: orderItems,
      totals: orderApiTotals,
      etaDate: pickUpDate,
      etaTime:
        isASAP || !cart.pickUpDate || cart.pickUpDate.length == 0
          ? getWindowedTimeString()
          : cart.pickUpTime,
      pickUpDate: pickUpDate,
      pickUpTime:
        isASAP || !cart.pickUpDate || cart.pickUpDate.length == 0
          ? getWindowedTimeString()
          : cart.pickUpTime,
      deliveryDate: deliveryDate,
      deliveryTime:
        isASAP || !cart.deliveryTime || cart.deliveryTime.length == 0
          ? getWindowedTimeString()
          : cart.deliveryTime,
      locationDetails: locationDetails,
      addressLine1: locationDetails.locationInfo.text,
      addressLine2: locationDetails.locationInfo.addressLine2,
      addressLine3: locationDetails.locationInfo.addressLine3,
      orderSource: "O",
      guestCount:
        isDineInOrder && guestUser && guestUser?.adultSize
          ? guestUser?.adultSize + guestUser?.kidzSize
          : 1,
      isReceiveTransMsg:1
    } as Cart;

    if (isDineInOrder) {
      orderDetails.tableId = tableId;
    }

    if (isDineInOrder && orderDetails && isContactlessDineinV2) {
      orderDetails.sortOrder = 1;
    } else {
      orderDetails.sortOrder = 0;
    }

     console.log({orderDetails});
    orderDetails.locationId.length > 0 &&
      !placeOrderLoading &&
      dispatch(placeOrder(orderDetails));
  };

  // console.log("mergedTotals:::813", mergedTotals);
  const restaurantDetail = useSelector(
    (state: RootState) => state.restaurant.currentRestaurantDetail
  );

  const sortOrder = useMemo(() => {
    if (restaurantDetail.dining == 3) {
      return 1;
    } else if (restaurantDetail.dining == 2) {
      return 0;
    } else {
      return null;
    }
  }, [restaurantDetail]);

  const getStatusForUpdateOrder = () => {
    if (
      isDineInOrder &&
      (restaurantDetail.dining == 3 || restaurantDetail.dining == 2)
    ) {
      return null;
    } else {
      return orderDetail?.status || null;
    }
  };

  const isTipAlreadyApplied = useMemo(() => {
    let tip: CartTotal | null = orderDetail
      ? getTotalByCode(orderDetail.totals, "3.0")
      : null;
    return tip ? parseInt(tip.value) : 0;
  }, [orderDetail]);

  const updateOrder = (type: UpdateOrderType, updatedTipTotals?: CartTotal) => {
    const getUpdatedTotal = (
      type: UpdateOrderType,
      updatedTipTotals?: CartTotal,
      cart?: Cart
    ) => {
      if (type == "TotalUpdate" && updatedTipTotals && cart) {
        if (isTipAlreadyApplied >= 0) {
          return parseInt(cart.orderTotal) + parseInt(updatedTipTotals.value);
        } else {
          return parseInt(cart.orderTotal) + parseInt(updatedTipTotals.value);
        }
      } else {
        return getTotalByCode(mergedTotals, "5")?.value || 0;
      }
    };

    const orderDetails = orderDetail
      ? ({
          orderId: orderDetail.orderId,
          orderNo: orderDetail.orderNo,
          orderDate: orderDetail.orderDate,
          orderTime: orderDetail.orderTime,
          locationId: orderDetail.locationId,
          customerId: orderDetail.customerId,
          deviceId: "",
          staffId: "",

          fullName: orderDetail.fullName,
          email: "",
          phone: orderDetail.phone,
          comment: orderNote,
          address: "",

          orderTypeId: orderDetail?.orderTypeId,
          totalItems: mergedItems.length.toString(),
          status: "25",
          deliveryStaffId: orderDetail?.deliveryStaffId,
          addressLine1: orderDetail?.addressLine1,
          addressLine2: orderDetail?.addressLine2,
          addressLine3: orderDetail?.addressLine3,

          etaTime:
            isASAP || !cart.pickUpDate || cart.pickUpDate.length == 0
              ? getWindowedTimeString()
              : cart.pickUpTime,
          etaDate: pickUpDate
            ? pickUpDate
            : orderDetail?.etaDate
            ? getDateString(orderDetail.etaDate)
            : null,
          pickUpTime:
            isASAP || !cart.pickUpDate || cart.pickUpDate.length == 0
              ? getWindowedTimeString()
              : cart.pickUpTime,
          pickUpDate: pickUpDate
            ? pickUpDate
            : orderDetail?.pickUpDate
            ? getDateString(orderDetail.pickUpDate)
            : null,
          deliveryTime:
            isASAP || !cart.deliveryTime || cart.deliveryTime.length == 0
              ? getWindowedTimeString()
              : cart.deliveryTime,
          deliveryDate: deliveryDate
            ? deliveryDate
            : orderDetail?.deliveryDate
            ? getDateString(orderDetail.deliveryDate)
            : null,

          items: mergedItems,
          totals: mergedTotals,
          orderTotal: getUpdatedTotal(
            type,
            updatedTipTotals,
            orderDetail as unknown as Cart
          ),

          removedItemId: [],
          removedOptionId: [],

          locationName: null,
          locationMedia: null,
          orderTypeGroup: null,
          addressId: null,
          tableId: tableId,
          sortOrder: sortOrder,
          orderSource: "O",
          isReceiveTransMsg:1
        } as EditOrderRequestCart)
      : null;

    if (type == "TotalUpdate" && orderDetails) {
      orderDetails.items = [];
    }
    orderDetails &&
      dispatch(
        updateOrderDetail({
          cart: orderDetails,
          refundAmount: null,
        })
      );
  };

  return [
    makeOrder,
    getDecimalPlacedPrice(totalItemsubTotal),
    getDecimalPlacedPrice(totalOrderPrice),
    getDecimalPlacedPrice(totalTaxAndCharges),
    getDecimalPlacedPrice(totalTaxAmount),
    getDecimalPlacedPrice(tipAmount),
    getDecimalPlacedPrice(packagingCharge),
    getDecimalPlacedPrice(discountAmount),
    getDecimalPlacedPrice(serviceCharge),
    updateOrder,
    getDecimalPlacedPrice(walletAmount),
    getDecimalPlacedPrice(redeemedWalletAmt),
    getDecimalPlacedPrice(convenienceFee),
  ];
};
